import { CircleNotch } from '@phosphor-icons/react/dist/ssr';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@skrym/shared/utils/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'shadow-xs',
        outline: 'bg-background border shadow-xs dark:bg-input/30',
        ghost: '',
        link: 'underline-offset-4 hover:underline',
      },
      color: {
        default: '',
        secondary: '',
        info: '',
        success: '',
        warning: '',
        error: '',
        done: '',
      },
      size: {
        default: 'h-9',
        small: 'h-8',
        large: 'h-10',
      },
      icon: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Handle icon sizes
      {
        size: 'small',
        icon: false,
        className:
          'px-[calc(--spacing(3)-var(--border-width))] has-[>svg]:px-[calc(--spacing(2.5)-var(--border-width))] gap-1.5',
      },
      {
        size: 'default',
        icon: false,
        className:
          'px-[calc(--spacing(4)-var(--border-width))] has-[>svg]:px-[calc(--spacing(3)-var(--border-width))]',
      },
      {
        size: 'large',
        icon: false,
        className:
          'px-[calc(--spacing(6)-var(--border-width))] has-[>svg]:px-[calc(--spacing(4)-var(--border-width))]',
      },
      {
        size: 'small',
        icon: true,
        className: 'px-[calc(--spacing(2)-var(--border-width))]',
      },
      {
        size: 'default',
        icon: true,
        className: 'px-[calc(--spacing(2.5)-var(--border-width))]',
      },
      {
        size: 'large',
        icon: true,
        className: 'px-[calc(--spacing(3)-var(--border-width))]',
      },

      // All the variant default ones
      {
        variant: 'default',
        color: 'default',
        className:
          'bg-primary hover:bg-primary/90 text-primary-foreground dark:text-primary-foreground',
      },
      {
        variant: 'default',
        color: 'secondary',
        className:
          'bg-secondary hover:not-dark:bg-secondary/70 hover:dark:brightness-110 text-secondary-foreground dark:text-secondary-foreground',
      },
      {
        variant: 'default',
        color: 'info',
        className:
          'bg-info hover:not-dark:bg-info/90 hover:dark:brightness-110 text-primary-foreground dark:text-secondary-foreground',
      },
      {
        variant: 'default',
        color: 'success',
        className:
          'bg-success hover:not-dark:bg-success/90 hover:dark:brightness-110 text-primary-foreground dark:text-secondary-foreground',
      },
      {
        variant: 'default',
        color: 'warning',
        className:
          'bg-warning hover:not-dark:bg-warning/90 hover:dark:brightness-110 text-primary-foreground dark:text-secondary-foreground',
      },
      {
        variant: 'default',
        color: 'error',
        className:
          'bg-error hover:not-dark:bg-error/90 hover:dark:brightness-110 text-primary-foreground dark:text-secondary-foreground',
      },
      {
        variant: 'default',
        color: 'done',
        className:
          'bg-done hover:not-dark:bg-done/90 hover:dark:brightness-110 text-primary-foreground dark:text-secondary-foreground',
      },

      // All the variant outlined ones
      {
        variant: 'outline',
        color: 'default',
        className: 'text-primary hover:bg-accent dark:hover:bg-input/50',
      },
      {
        variant: 'outline',
        color: 'secondary',
        className: 'text-foreground/80 hover:bg-accent dark:hover:bg-input/50',
      },
      {
        variant: 'outline',
        color: 'info',
        className:
          'border-info/50 text-info dark:text-info-foreground hover:bg-info-muted/50 dark:hover:bg-info-muted/40',
      },
      {
        variant: 'outline',
        color: 'success',
        className:
          'border-success/50 text-success dark:text-success-foreground hover:bg-success-muted/30 dark:hover:bg-success-muted/40',
      },
      {
        variant: 'outline',
        color: 'warning',
        className:
          'border-warning/50 text-warning dark:text-warning-foreground hover:bg-warning-muted/30 dark:hover:bg-warning-muted/40',
      },
      {
        variant: 'outline',
        color: 'error',
        className:
          'border-error/50 text-error dark:text-error-foreground hover:bg-error-muted/30 dark:hover:bg-error-muted/40',
      },
      {
        variant: 'outline',
        color: 'done',
        className:
          'border-done/50 text-done dark:text-done-foreground hover:bg-done-muted/30 dark:hover:bg-done-muted/40',
      },

      // All the variant ghost ones
      {
        variant: 'ghost',
        color: 'default',
        className: 'hover:bg-accent dark:hover:bg-accent/50',
      },
      {
        variant: 'ghost',
        color: 'secondary',
        className: 'text-foreground/80 hover:bg-accent dark:hover:bg-accent/50',
      },
      {
        variant: 'ghost',
        color: 'info',
        className:
          'text-info dark:text-info-foreground hover:bg-info-muted/50 dark:hover:bg-info-muted/40',
      },
      {
        variant: 'ghost',
        color: 'success',
        className:
          'text-success dark:text-success-foreground hover:bg-success-muted/30 dark:hover:bg-success-muted/40',
      },
      {
        variant: 'ghost',
        color: 'warning',
        className:
          'text-warning dark:text-warning-foreground hover:bg-warning-muted/30 dark:hover:bg-warning-muted/40',
      },
      {
        variant: 'ghost',
        color: 'error',
        className:
          'text-error dark:text-error-foreground hover:bg-error-muted/30 dark:hover:bg-error-muted/40',
      },
      {
        variant: 'ghost',
        color: 'done',
        className:
          'text-done dark:text-done-foreground hover:bg-done-muted/30 dark:hover:bg-done-muted/40',
      },

      // All the variant link ones
      {
        variant: 'link',
        color: 'default',
        className: '',
      },
      {
        variant: 'link',
        color: 'secondary',
        className: 'text-foreground/80',
      },
      {
        variant: 'link',
        color: 'info',
        className: 'text-info dark:text-info-foreground',
      },
      {
        variant: 'link',
        color: 'success',
        className: 'text-success dark:text-success-foreground',
      },
      {
        variant: 'link',
        color: 'warning',
        className: 'text-warning dark:text-warning-foreground',
      },
      {
        variant: 'link',
        color: 'error',
        className: 'text-error dark:text-error-foreground',
      },
      {
        variant: 'link',
        color: 'done',
        className: 'text-done dark:text-done-foreground',
      },
    ],
    defaultVariants: {
      variant: 'default',
      color: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

/**
 * Button component that supports various variants, colors, sizes, and loading states.
 *
 * It is based on {@link https://ui.shadcn.com/docs/components/button | Shadcn's Button component}.
 *
 * Currently, the loading indicator does not work when using the `asChild` prop.
 * This is since the `Slot` component requires a single child, and the loading indicator is an additional child.
 *
 * @see {@link https://ui.shadcn.com/docs/components/button}
 */
function Button(props: Readonly<ButtonProps>) {
  const {
    className,
    variant,
    color,
    size,
    icon = false,
    loading = false,
    disabled = false,
    children,
    style,
    asChild = false,
    type = 'button', // Default type is 'button' to prevent form submission
    ...rest
  } = props;

  // We can't support loading with `asChild` at the moment, since it requires only one child
  if (asChild) {
    return (
      <Slot
        data-slot="button"
        data-loading={!!loading}
        style={{
          // Used to decrease the padding if the button has a border
          '--border-width': variant === 'outline' ? '1px' : '0px',
          ...style,
        }}
        className={cn(buttonVariants({ variant, color, size, icon, className }))}
        {...rest}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      data-slot="button"
      data-loading={!!loading}
      style={{
        // Used to decrease the padding if the button has a border
        '--border-width': variant === 'outline' ? '1px' : '0px',
        ...style,
      }}
      className={cn(buttonVariants({ variant, color, size, icon, className }))}
      disabled={disabled || loading}
      type={type}
      {...rest}
    >
      {loading && <CircleNotch weight="bold" className="animate-spin" />}

      {/* If icon and loading, hide the icon itself */}
      {!(loading && icon) && children}
    </button>
  );
}

export { Button, buttonVariants };
